import { api } from "@shared/api/client";
import type {
  CalidadObjetivo,
  CalidadObjetivoCloneSource,
  CalidadObjetivoEtapa,
  CalidadObjetivoFilters,
  CalidadObjetivoMeta,
  CalidadObjetivoPayload,
} from "../types/objetivos";

const BASE = "/certificaciones/iso-9001-14001/objetivos";

type ObjetivosResponse = {
  data: CalidadObjetivo[];
  meta: CalidadObjetivoMeta;
};

type ResourceResponse<T> = {
  data: T;
};

export async function fetchCalidadObjetivos(filters: CalidadObjetivoFilters, signal?: AbortSignal): Promise<ObjetivosResponse> {
  const params: Record<string, string | boolean> = {};

  if (filters.anio) params.anio = filters.anio;
  if (filters.search) params.search = filters.search;
  if (filters.cerrado === "si") params.cerrado = true;
  if (filters.cerrado === "no") params.cerrado = false;

  const { data } = await api.get<ObjetivosResponse>(BASE, { params, signal });
  return data;
}

export async function fetchObjetivoCloneSource(anio: number): Promise<CalidadObjetivoCloneSource[]> {
  const { data } = await api.get<{ data: CalidadObjetivoCloneSource[] }>(`${BASE}/clone-source`, {
    params: { anio },
  });
  return data.data;
}

export async function createCalidadObjetivo(payload: CalidadObjetivoPayload): Promise<CalidadObjetivo> {
  const { data } = await api.post<ResourceResponse<CalidadObjetivo>>(BASE, payload);
  return data.data;
}

export async function updateCalidadObjetivo(id: number, payload: CalidadObjetivoPayload): Promise<CalidadObjetivo> {
  const { data } = await api.put<ResourceResponse<CalidadObjetivo>>(`${BASE}/${id}`, payload);
  return data.data;
}

export async function deleteCalidadObjetivo(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

export async function createObjetivoEtapa(objetivoId: number, payload: unknown): Promise<CalidadObjetivoEtapa> {
  const { data } = await api.post<ResourceResponse<CalidadObjetivoEtapa>>(`${BASE}/${objetivoId}/etapas`, payload);
  return data.data;
}

export async function updateObjetivoEtapa(objetivoId: number, etapaId: number, payload: unknown): Promise<CalidadObjetivoEtapa> {
  const { data } = await api.put<ResourceResponse<CalidadObjetivoEtapa>>(`${BASE}/${objetivoId}/etapas/${etapaId}`, payload);
  return data.data;
}

export async function deleteObjetivoEtapa(objetivoId: number, etapaId: number): Promise<void> {
  await api.delete(`${BASE}/${objetivoId}/etapas/${etapaId}`);
}

export async function uploadObjetivoAnexos(objetivoId: number, files: FileList | File[]): Promise<void> {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("attachments[]", file));
  await api.post(`${BASE}/${objetivoId}/anexos`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function objetivoAnexoDownloadUrl(objetivoId: number, anexoId: number): string {
  return `${api.defaults.baseURL}${BASE}/${objetivoId}/anexos/${anexoId}/download`;
}

export async function deleteObjetivoAnexo(objetivoId: number, anexoId: number): Promise<void> {
  await api.delete(`${BASE}/${objetivoId}/anexos/${anexoId}`);
}
